import { DataAction, Payload } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { EventEmitter, inject, Injector, Optional, Type } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { createSelector, StateToken, Store } from '@ngxs/store';
import { produce } from 'immer';
import * as moment from 'moment';
import { ConfirmationService } from 'primeng/api';
import { BehaviorSubject, combineLatest, Observable, of, firstValueFrom, from, lastValueFrom } from 'rxjs';
import { catchError, filter, first, map, mergeMap, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import {
    IAdvanceStageResult,
    ICurrentCaseByCategory,
    IProductCategoryAvailability,
    IProductCategoryContext,
    IProductCategoryMetadata,
    IProductContextAssessmentStatus,
    IProductContextCase,
    IProductContextCaseStatus,
    IProductContextMetadataOwner,
    IProductInterviewConfig,
    IProductSmartActionReference,
    IProductStage
} from 'src/app/models/case';
import { MMRRecord } from 'src/app/models/member';
import { MemberNote } from 'src/app/models/note';
import { Product } from 'src/app/models/product';
import { Role } from 'src/app/models/role';
import { SignalREvent } from 'src/app/models/signalr';
import { ProductOrchestratorService } from 'src/app/services/+products/product-orchestrator.service';
import { TaskService } from 'src/app/services/task/task.service';
import { IInterviewDataProvider } from 'src/app/shared/interview-new/instances/data-providers/data-providers.models';
import {
    IDisqualificationReason,
    IInterviewResult,
    InterviewEventType,
    IVariableEvaluationModel
} from 'src/app/shared/interview-new/models/interview-handler.models';
import { AssessmentType, IInterviewConfig } from 'src/app/shared/interview-new/models/interview.models';
import { FeatureService } from 'src/app/shared/preview-feature/preview-feature.service';

import { TaskOrchestratorService } from 'src/app/services/task-orchestrator/task-orchestrator.service';
import {
    SmartActionContext,
    SmartActionContextItem,
    SmartActionContextType
} from '../../models/smart-actions.models';
import { AlertService, MessageSeverity } from '../alert.service';
import { CaseService } from '../case/case.service';
import { ConfigurationService } from '../configuration/configuration.service';
import { InjectorInstance } from '../global/global.service';
import { MemberNoteService } from '../member-note/member-note.service';
import { MemberService } from '../member/member.service';
import { ProductContextService } from '../product-context/product-context.service';
import { UserService } from '../user/user.service';
import { Utilities } from '../utilities';
import {
    AssignCaseOwnershipRequest,
    InterviewInProgressStages,
    ProductCategoryNames,
    RollbackCaseStageRequestData
} from './../../models/case';
import { INotification, Notifications } from './../../models/notifications';
import { RoleList } from './../../models/role';
import {
    AssessmentMode,
    ExecutionMode,
    IInterviewFinalizer,
    IInterviewHandler,
    IInterviewSettings,
    InterviewType
} from './../../shared/interview-new/models/interview-handler.models';
import { BaseProductInterviewData } from './_base-product-interview-data';
import { BaseProductStateModel, MultipleBaseProductStateModel } from './base-product-state.models';
import { SmartActionsStandard, StageTrackerStandardContexts } from './product-smart-actions';
import { ISmartIconSettings, SmartIconSettings } from 'src/app/models/smart-icons-settings';
import { IContextData, IContextDataHost, IContextDataItem } from 'src/app/shared/context-data/context-data.models';
import { ContextDataService } from 'src/app/shared/context-data/context-data.service';
import { ConfigsService } from '../configs/configs.service';
import { ITaskCommandWrapper } from '../task/task.state.models';


export class ProductSelectorExtentions {

    static sourceProduct<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.sourceProduct;

        });
    }

    static productCategoryContext<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCategoryContext;
        });
    }

    static metadata<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCategoryContext.metadata;
        });
    }


    static cases<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: MultipleBaseProductStateModel) => {
            return state.productCategoryContext.cases || [];
        });
    }

    static currentCase<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return BaseProductService.getCase(state.productCategoryContext);
        });
    }


    static categoryDisplayName<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCategoryContext.categoryDisplayName;

        });
    }

    static noCaseMessage<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCategoryContext.noCaseMessage;

        });
    }

    static icon<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCategoryContext.icon;

        });
    }

    static owner<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCategoryContext.owner;
        });
    }
    static currentStage<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            const $case = BaseProductService.getCase(state.productCategoryContext);
            return $case.stage;
        });
    }

    static notifications<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.notifications;
        });
    }

    static status<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCategoryContext.status;
        });
    }

    static caseStage<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return BaseProductService.findStage(state.productCategoryContext);
        });
    }

    static caseAssessment<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            const $case = BaseProductService.getCase(state.productCategoryContext);
            return $case.stage.assessment;
        });
    }

    static stages<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCategoryContext.stages;
        });
    }

    static smartActions<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCategoryContext.metadata.smartActions;
        });
    }
    static dashboard<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            const settings = state.productCategoryContext.metadata.smartIcons as ISmartIconSettings[];
            return (settings || []).map((settings: ISmartIconSettings) => new SmartIconSettings(settings));
        });
    }

    static productInterviews<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCategoryContext.metadata.interviews;

        });
    }
}



export interface CurrentComponentOptions {
    extraTitle: string;
}
export interface IRollbackAction {
    allow: boolean;
    action: SmartActionContextItem;
}

export abstract class BaseProductService<T extends BaseProductStateModel> extends NgxsDataRepository<T> implements IInterviewFinalizer, IContextDataHost {


    interviewDataProvider: IInterviewDataProvider;

    abstract get stateToken(): StateToken<T>;

    notInterestedActionId = 'NotInterested';
    notQualifiedActionId = 'NotQualified';

    defaultInterviewSettings = {
        isPrimary: true,
        showInterviewMetadata: false,
        hideFinishButton: false,
        hideOptions: false,
        hideProducts: true,
        pageSize: 5,
        interviewContextFunc: (() => {
            return {
                extraData: 'Custom data from interview'
            };
        }).bind(this)
    } as IInterviewSettings;

    evaluate$ = new EventEmitter<any>();
    protected _smartActionContext$ = new BehaviorSubject<SmartActionContext>(null);
    smartActionContext$ = this._smartActionContext$.asObservable().pipe(shareReplay(1));

    loadCustomView$ = new BehaviorSubject<string>(null);

    currentCustomViewMetadata: any;

    signalR$ = this.memberService.signalR$.pipe(filter((e: SignalREvent) => this.signalRFilter(e)));
    routeEnd$$ = this.router.events.subscribe((event: Event) => {

        if (event instanceof NavigationEnd) {
            this.updateAllowedActions$.next(null);
        }
    });

    forceLoadProductCase$ = new EventEmitter<boolean>(true);

    protected noteService: MemberNoteService;
    interviewData: BaseProductInterviewData;
    urlSubscription: any;



    updateAllowedActions$ = new EventEmitter<any>();

    mmr$: Observable<MMRRecord>;

    get interviewType() {
        switch (this.productCategory) {
            case 'Community':
                return InterviewType.Community;
            case 'Medicaid':
                return InterviewType.Medicaid;
            case 'SNAP':
                return InterviewType.SNAP;
            case 'LIS':
                return InterviewType.LIS;
            case 'VA':
                return InterviewType.Veteran;
        }
    }

    static productCase(instance: BaseProductService<BaseProductStateModel>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.productCase;

        });
    }

    static currentCustomView(instance: BaseProductService<BaseProductStateModel>) {
        return createSelector([instance.stateToken], (state: BaseProductStateModel) => {
            return state.currentCustomView;

        });
    }

    // static get sourceProduct() {
    //     return createSelector([], (state: BaseProductStateModel) => {
    //         return state.sourceProduct;

    //     });
    // }



    static getCase(context: IProductCategoryContext) {
        return context.cases && context.cases[0];
    }


    /************************************************************************ */
    static findStage(productCategoryContext: IProductCategoryContext, name: string = null) {
        const $case = BaseProductService.getCase(productCategoryContext);
        const caseStageName = name || $case?.stage.destination;
        const stages = productCategoryContext.stages;
        const currentStage = $case?.stage;
        let result = stages?.find(item => item.name === caseStageName);
        result = {
            ...result,
            createdOn: (!name && currentStage ? currentStage.createdOn : null),
            metadata: (!name && currentStage ? currentStage.metadata : null),
            source: (!name && currentStage ? currentStage.source : null),
            destination: (!name && currentStage ? currentStage.destination : null),
        };
        return result;
    }

    static stageIsLast(productCategoryContext: IProductCategoryContext, name: string = null): boolean {
        const $case = BaseProductService.getCase(productCategoryContext);
        const caseStageName = name || $case.stage.destination;
        const stages = productCategoryContext.stages;

        const sort = (a, b) => a.order > b.order ? -1 : 1;
        const last = [...stages].sort(sort)[0];

        return last.name === caseStageName;
    };


    findCurrentStage() {
        return BaseProductService.findStage(this.productCategoryContextSync);
    }



    currentStageIsLast(): boolean {
        return BaseProductService.stageIsLast(this.productCategoryContextSync);
    };
    /************************************************************************ */


    get customViewSync(): string {
        return this.snapshot.currentCustomView;
    }


    get sourceProductSync(): Product {
        return this.snapshot.sourceProduct;
    }


    get status$(): Observable<IProductContextCaseStatus> {
        return this.store.select<IProductContextCaseStatus>(ProductSelectorExtentions.status(this));
    }

    get statusSync(): IProductContextCaseStatus {
        return this.store.selectSnapshot<IProductContextCaseStatus>(ProductSelectorExtentions.status(this));
    }

    get caseOwner$(): Observable<IProductContextMetadataOwner> {
        return this.store.select<IProductContextMetadataOwner>(ProductSelectorExtentions.owner(this));
    }

    get caseOwnerSync(): IProductContextMetadataOwner {
        return this.store.selectSnapshot<IProductContextMetadataOwner>(ProductSelectorExtentions.owner(this));
    }

    get sourceProduct$(): Observable<Product> {
        return this.store.select<Product>(ProductSelectorExtentions.sourceProduct(this));
    }

    get productCategoryContext$(): Observable<IProductCategoryContext> {
        return this.store.select<IProductCategoryContext>(ProductSelectorExtentions.productCategoryContext(this));
    }

    get productCategoryContextSync(): IProductCategoryContext {
        return this.store.selectSnapshot<IProductCategoryContext>(ProductSelectorExtentions.productCategoryContext(this));
    }

    get metadataSync(): any/*IProductCategoryMetadata*/ {
        return this.store.selectSnapshot<any>(ProductSelectorExtentions.metadata);
    }
    get metadata$(): Observable<any> {
        return this.store.select<any>(ProductSelectorExtentions.metadata);
    }

    get currentCaseSync(): IProductContextCase {
        return this.store.selectSnapshot<IProductContextCase>(ProductSelectorExtentions.currentCase(this));
    }


    get currentCase$(): Observable<IProductContextCase> {
        return this.store.select<IProductContextCase>(ProductSelectorExtentions.currentCase(this));
    }

    get caseStageSync(): IProductStage {
        return this.store.selectSnapshot<IProductStage>(ProductSelectorExtentions.caseStage(this));
    }
    get caseStage$(): Observable<IProductStage> {
        return this.store.select<IProductStage>(ProductSelectorExtentions.caseStage(this));
    }

    get notifications$(): Observable<INotification[]> {
        return this.store.select<INotification[]>(ProductSelectorExtentions.notifications(this));
    }

    get notificationsSync(): INotification[] {
        return this.store.selectSnapshot<INotification[]>(ProductSelectorExtentions.notifications(this));
    }

    get productInterviewsSync(): IProductInterviewConfig[] {
        return this.store.selectSnapshot<IProductInterviewConfig[]>(ProductSelectorExtentions.productInterviews(this));
    }

    get productInterviews$(): Observable<IProductInterviewConfig[]> {
        return this.store.select<IProductInterviewConfig[]>(ProductSelectorExtentions.productInterviews(this));
    }

    get caseAssessmentSync(): IProductContextAssessmentStatus {
        return this.store.selectSnapshot<IProductContextAssessmentStatus>(ProductSelectorExtentions.caseAssessment(this));
    }
    get caseAssessment$(): Observable<IProductContextAssessmentStatus> {
        return this.store.select<IProductContextAssessmentStatus>(ProductSelectorExtentions.caseAssessment(this));
    }


    get iconSync(): string {
        return this.store.selectSnapshot<string>(ProductSelectorExtentions.icon(this));
    }
    get icon$(): Observable<string> {
        return this.store.select<string>(ProductSelectorExtentions.icon(this));
    }

    get noCaseMessageSync(): string {
        return this.store.selectSnapshot<string>(ProductSelectorExtentions.noCaseMessage(this));
    }
    get noCaseMessage$(): Observable<string> {
        return this.store.select<string>(ProductSelectorExtentions.noCaseMessage(this));
    }

    get categoryDisplayNameSync(): string {
        return this.store.selectSnapshot<string>(ProductSelectorExtentions.categoryDisplayName(this));
    }
    get categoryDisplayName$(): Observable<string> {
        return this.store.select<string>(ProductSelectorExtentions.categoryDisplayName(this));
    }

    get stagesSync(): IProductStage[] {
        return this.store.selectSnapshot<IProductStage[]>(ProductSelectorExtentions.stages(this));
    }
    get stages$(): Observable<IProductStage[]> {
        return this.store.select<IProductStage[]>(ProductSelectorExtentions.stages(this));
    }

    get smartActionsSync(): IProductSmartActionReference[] {
        return this.store.selectSnapshot<IProductSmartActionReference[]>(ProductSelectorExtentions.smartActions(this));
    }
    get smartActions$(): Observable<IProductSmartActionReference[]> {
        return this.store.select<IProductSmartActionReference[]>(ProductSelectorExtentions.smartActions(this));
    }

    get dashboardSync(): SmartIconSettings[] {
        return this.store.selectSnapshot<SmartIconSettings[]>(ProductSelectorExtentions.dashboard(this));
    }
    get dashboard$(): Observable<ISmartIconSettings[]> {
        return this.store.select<ISmartIconSettings[]>(ProductSelectorExtentions.dashboard(this));
    }

    private _interviews: IInterviewConfig[];
    get interviews() {
        return this._interviews;
    }



    abstract signalRFilter(e: SignalREvent): boolean;

    protected get rolesForCaseStageRollback() {
        return ['ADMIN'];
    }

    taskService: TaskService;

    protected productOrchestratorService: ProductOrchestratorService

    abstract get interviewDataProviderToken(): string;

    get publicProductCategoryContext() {
        return this.productCategoryContextSync;
    }


    public configsService = inject(ConfigsService);
    constructor(
        @Optional() public productCategory: keyof typeof ProductCategoryNames,
        public memberService: MemberService,
        public userService: UserService,
        protected caseService: CaseService,
        protected productContextService: ProductContextService,
        public confirmationService: ConfirmationService,
        public router: Router,
        public route: ActivatedRoute,
        protected alertService: AlertService,
        interviewDataType: Type<BaseProductInterviewData>,
        protected store: Store,
        protected featureService: FeatureService,
        public interviewHandler: IInterviewHandler,
        public taskOrchestratorService: TaskOrchestratorService
    ) {
        super();

        this.productOrchestratorService = InjectorInstance.get(ProductOrchestratorService);
        this.interviewData = new interviewDataType(this, this.userService);
        this.noteService = InjectorInstance.get(MemberNoteService);
    }


    async ngxsAfterBootstrap() {
        await combineLatest([this.store.select<string>(UserService.userIdentifier), this.store.select<boolean>(ConfigurationService.initializedSelector)]).pipe(filter(([userIdentifier, configInitialized]) => !!userIdentifier && configInitialized), first()).toPromise();

        super.ngxsAfterBootstrap();
        this.initializeSmartActionsEvaluator();
        this.initializeListeners();
    }


    async initializeListeners() {

        this.mmr$ = this.memberService.mmr$;

        this.initializeProductListener();
        this.initializeProductCaseListener();
    }

    initializeProductListener() {
        const tenantProducts$ = this.store.select<Product[]>(MemberService.products);
        combineLatest([tenantProducts$, this.currentCase$]).pipe(
            filter(([products]) => !!products)
        )
            .subscribe(([products, productCaseNew]) => {
                let product = products.find(productItem => productItem.id === productCaseNew?.productId);
                if (!product) {
                    product = products.find(productItem => productItem.productCategory.name.toUpperCase() === this.productCategory.toUpperCase());
                }

                this.setSourceProduct(product);
            });
    }

    initializeProductCaseListener() {
        const forceLoadProductCase$ = this.forceLoadProductCase$.pipe(startWith(null));
        combineLatest([this.memberService.initialized$, this.memberService.commands$, forceLoadProductCase$]).pipe(filter(([initialized]) => !!initialized)).subscribe(async (_) => {
            return this.getCurrentProductContext();
        });
    }

    protected async getCurrentProductContext(context?: any): Promise<IProductContextCase> {
        await this.getCurrentProductContextCore(context, true);
        this.contextService.refreshData(this.productCategory);
        return this.currentCaseSync;
    }

    protected async getCurrentProductContextCore(context?: any, save = false): Promise<IProductCategoryContext> {
        const base64 = this.store.selectSnapshot<string>(MemberService.base64);

        let productCategoryContext: IProductCategoryContext
        if (base64) {

            const { tenantId } = MemberService.parseBase64(base64);
            const newRequest: ICurrentCaseByCategory = {
                ...context,
                memberId: this.memberService.memberIdSync,
                user: this.userService.userIdentifierSync,
                tenantId: this.memberService.tenantIdSync,
                productCategoryName: this.productCategory,
                telephonyQueryParams: this.memberService.telephonyParamsService.queryParamsSync,
                role: this.userService.currentRoleNameSync,
                tasks: (this.memberService.trackingCommandsSync || [])
                                    .map(_ => _ as ITaskCommandWrapper).filter(_ => _.task)
                                    .map(cmd => ({
                                        command: cmd.id,
                                        task: cmd.task.id,
                                        order: cmd.task.executionData.executionOrder,
                                        status: cmd.task.executionData.status.value,
                                        metadata: cmd.task.executionData.metadata
                                    }))
            };

            productCategoryContext = await this.getProductContext(newRequest);
        }

        if (save) {
            this.setProductCategoryContext(productCategoryContext);
        }
        return productCategoryContext;
    }

    async getProductContext(req: ICurrentCaseByCategory): Promise<IProductCategoryContext> {

        req = { ...req, ...this.featureService.activeFeatures() };
        return await firstValueFrom(this.productContextService.getProductContext(req.tenantId, req).pipe(first()));
    }

    public allowRollback(userRole: Role, callback: (action: SmartActionContextItem) => any) {

        const rollBackAction = SmartActionsStandard.rollbackCaseStage(this);

        const allow = rollBackAction && ((userRole && userRole.inRoles(this.rolesForCaseStageRollback)) || (this.memberService.tenantIdSync && this.memberService.tenantIdSync.toUpperCase() === 'TRAINING'));

        if (allow === true) {
            callback(rollBackAction);
        }
    }

    isSameCategory(interviewCategory: string): boolean {
        return this.productCategory.toUpperCase() == interviewCategory?.toUpperCase();
    }

    getAvailability(avail: IProductCategoryAvailability): IProductCategoryAvailability {
        return avail;
    }

    async customizeAvailability(avail: IProductCategoryAvailability): Promise<IProductCategoryAvailability> {

        return Promise.resolve(avail);
    }

    getInterview(interviewType: string) {
        return this.interviews?.find(_ => _.type.toUpperCase() === interviewType.toUpperCase());
    }

    protected isProductDisabled(): boolean {
        return this.memberService.memberSync && this.memberService.memberSync.hContract && this.memberService.memberSync.hContract.startsWith('S') && this.memberService.tenantIdSync === 'UHC';
    }

    public showSendAr(): Promise<boolean> {
        return Promise.resolve(false);
    }

    async getCaseAsync(): Promise<IProductContextCase> {
        const productCategoryContext = await this.productCategoryContext$.pipe(filter(_ => !!_), take(1)).toPromise();
        const $case = BaseProductService.getCase(productCategoryContext);
        if ($case) {
            const productCase = await this.currentCase$.pipe(filter(_ => !!_), take(1)).toPromise();
            return Promise.resolve(productCase);
        }

        return Promise.resolve(null);
    }

    async getOrAddCase(assessmentType?: AssessmentType): Promise<IProductContextCase> {
        const productCase = await this.getCaseAsync();

        if (await this.shouldCreateCase(productCase, assessmentType)) {
            return await this.addCase().pipe(first()).toPromise();
        }

        return Promise.resolve(productCase);
    }

    protected async allowCaseCreate(productCase: IProductContextCase, assessmentType: AssessmentType, userRole: Role = null): Promise<boolean> {
        let hasDriver = this.taskOrchestratorService.tasksSync?.findIndex(t => t.id == 'DRIVER.INTERVIEW') != -1;

        return !hasDriver && this.statusSync.active === true;
    }

    protected allowCaseCreateIfExists(productCase: IProductContextCase, assessmentType: AssessmentType, userRole: Role = null): boolean {
        const originDate = productCase.stage.createdOn && moment(productCase.stage.createdOn);
        const months = originDate && moment().diff(originDate, 'month');

        return this.currentStageIsLast() && (months >= 3 || (userRole && userRole.isSupervisor));
    }

    async shouldCreateCase(productCase: IProductContextCase, assessmentType: AssessmentType): Promise<boolean> {
        const caseExists = (!!productCase && !!productCase.id);
        const sourceProduct = (await this.sourceProduct$.pipe(take(1)).toPromise());
        const isProductEnabled = sourceProduct && sourceProduct.active;

        if (assessmentType === AssessmentType.Eligibility || assessmentType === AssessmentType.Education) {
            const userRole = await lastValueFrom(this.userService.currentRole$.pipe(take(1)));
            const allowCaseCreate = await this.allowCaseCreate(productCase, assessmentType, userRole);
            const _allowNewCaseCreation = !caseExists && allowCaseCreate;
            const _allowNewCaseCreationIfExists = caseExists && isProductEnabled && allowCaseCreate && (this.allowCaseCreateIfExists(productCase, assessmentType, userRole));
            return Promise.resolve(_allowNewCaseCreation || _allowNewCaseCreationIfExists);
        } else {
            return Promise.resolve(false);
        }
    }

    protected getAllowedActions(productCase: IProductContextCase, sourceProduct: Product, userRole?: Role): Observable<SmartActionContext> {
        return StageTrackerStandardContexts.noAction(this);
    }

    private getAllowedActionsBase(productCaseNew: IProductContextCase, sourceProduct: Product, userRole?: Role): Observable<SmartActionContext> {

        return this.getAllowedActions(productCaseNew, sourceProduct, userRole).pipe(mergeMap(context => {

            const observableSmartActions = this.smartActionsSync?.map(smartAction => SmartActionsStandard.external(this, smartAction)) || [];
            const smartActionPromises = observableSmartActions.map(smartAction => (smartAction as Promise<SmartActionContextItem>)?.then ? smartAction as Promise<SmartActionContextItem> : Promise.resolve(smartAction as SmartActionContextItem))
            const allPromise = Promise.all(smartActionPromises);

            return combineLatest([Promise.resolve(context), allPromise]);

        }), switchMap(([context, resolvedActions]) => {
            const formattedExternalActions = resolvedActions.filter(action => !!action); //remove null smart actions the rollback have a condition which when not satisfied returns null

            context.actions = [...formattedExternalActions, ...context?.actions || []]
            context.actions = context.actions.sort(this.orderSmartActions);
            context.actions = this.removeUnnecessarySeparators(context.actions);

            return of(context);
        }));
    }

    private orderSmartActions(a: SmartActionContextItem, b: SmartActionContextItem) {
        const aOrder = a.order || 9999;
        const bOrder = b.order || 9999;

        return aOrder > bOrder ? 1 : (aOrder < bOrder ? -1 : 0);
    }

    private removeUnnecessarySeparators(actions: SmartActionContextItem[]): SmartActionContextItem[] {

        return (actions || []).reduce<SmartActionContextItem[]>((previous, current, index, items) => {
            let result = previous;

            const emptyResult = result.length === 0;
            const previousIsSeparator = (!emptyResult && result[result.length - 1].separator);
            const atEnding = (index === items.length - 1);

            if (!current.separator || !(emptyResult || previousIsSeparator || atEnding)) {
                result.push(current);
            } else if (atEnding && !emptyResult && result[result.length - 1]?.separator) {
                result = result.slice(0, -1);
            }

            return result;
        }, []);
    }

    protected initializeSmartActionsEvaluator() {
        // need to use the store because the mmr property in member service is not been created until after.
        const mmr$ = this.store.select<MMRRecord>(MemberService.mmr);

        combineLatest([this.currentCase$, mmr$, this.sourceProduct$, this.userService.currentRole$, this.updateAllowedActions$.pipe(startWith(null))]).pipe(switchMap(([productCaseNew, mmr, sourceProduct, userRole]) => {
            return this.getAllowedActionsBase(productCaseNew, sourceProduct, userRole);
        }))
            .subscribe(smartActionContext => {
                this._smartActionContext$.next(smartActionContext);
            });
    }

    smartActionContextByContext(contextType?: SmartActionContextType): Observable<SmartActionContext> {
        return this.smartActionContext$.pipe(map(context => {
            if (!!context) {
                context.actions = context.actions.filter(action => !action.actionMetadata || !action.actionMetadata.contextType || !contextType || contextType === action.actionMetadata.contextType);
            }

            return context;
        }));
    }

    async addNote(message: string) {

        const note = new MemberNote({ noteText: message });
        note.memberId = this.memberService.memberSync.id;

        await this.noteService.addOrUpdate(this.memberService.tenantIdSync, note).toPromise();
    }

    formatInterviewResult(interviewHandler: IInterviewHandler): Observable<IInterviewResult> {
        return interviewHandler.events.evaluationResult$.asObservable().pipe(map(evaluationResult => evaluationResult.interviews[0]));
    }

    protected parseInterviewResult(interviewResult: IInterviewResult, status?: string, disqualificationReason?: IDisqualificationReason) {
        status = status || interviewResult.evaluationStatus;

        if (!disqualificationReason) {
            const disqualificationReasons = interviewResult.qualificationResultItems
                .map(a => a.disqualifications)
                .reduce((prev, current) => [...prev, ...current], []);

            disqualificationReason = (disqualificationReasons || []).find(reason => reason.severity.toLocaleUpperCase() === 'ERROR');
        }

        return { status, disqualificationReason };
    }

    async finalizeInterview(interviewHandler: IInterviewHandler): Promise<IAdvanceStageResult | void> {
        const interviewResult = await this.formatInterviewResult(interviewHandler).pipe(first()).toPromise();

        const { status, disqualificationReason } = this.parseInterviewResult(interviewResult);

        await this.interviewAdvanceCaseStage(status, disqualificationReason, interviewHandler);

        return this.productOrchestratorService.refreshAll();
    }

    async interviewAdvanceCaseStage(status: string, disqualificationReason: IDisqualificationReason, interviewHandler: IInterviewHandler) {
        if (await this.canInterviewAdvanceStage(status, disqualificationReason, interviewHandler)) {

            const action = status?.toUpperCase() === 'QUALIFIED' ? 'Next' : ((disqualificationReason && disqualificationReason.resultCode) || 'Unknown');
            const reason = status?.toUpperCase() === 'QUALIFIED' ? null : (disqualificationReason && disqualificationReason.resultCode);
            const metadata = status?.toUpperCase() === 'QUALIFIED' ? null : (disqualificationReason && disqualificationReason.metadata);

            return this.advanceStage(action, reason, metadata, false);
        }
    }

    async canInterviewAdvanceStage(status: string, disqualificationReason: IDisqualificationReason, interviewHandler: IInterviewHandler) {
        const userRole = this.userService.currentRoleSync;
        return this.caseStageSync.assessmentType !== AssessmentType.Enrollment.toString()
            || !InterviewInProgressStages.find(stageItem => stageItem === this.caseStageSync.name)
            ||
            (
                !!disqualificationReason && userRole.inRole(RoleList.Advocate)
            );
    }

    async advanceStage(actionId?: any, reason?: any, metadata: any = {}, refresh: boolean = true) {
        try {
            const productCase = this.currentCaseSync;

            let payload: { [key: string]: any } = {};
            if (reason != null) {
                if (typeof reason === 'string') {
                    payload.reason = reason;
                } else {
                    payload = reason as { [key: string]: any };
                }
            }

            payload = { ...payload, ...metadata };


            const result = await this.caseService.advanceStage(this.memberService.base64Sync, productCase.externalId, actionId || 'Next', payload).pipe(first()).toPromise();

            if (refresh == true) {
                await this.refreshCase().pipe(first()).toPromise();
            }

            return result;

        } catch (error) {
            this.alertService.stopLoadingMessage();
            this.alertService.showStickyMessage(
                'Case Error',
                `$Unable to advance the case\r\nError: "${Utilities.getHttpResponseMessages(
                    error
                )}"`,
                MessageSeverity.error,
                error
            );

            return 'error';
        }
    }

    instanciateSecondaryInterviewDataProvider(injector: Injector): IInterviewDataProvider {
        if (!this.interviewDataProvider) {
            this.interviewDataProvider = injector.get(this.interviewDataProviderToken) as IInterviewDataProvider;
        }
        return this.interviewDataProvider;
    }

    async initializeSecondaryInterview(externalInterviewSettings: IInterviewSettings, injector: Injector) {
        const allowEligilibity = await this.allowEligibility();
        if (!allowEligilibity) {
            return;
        }

        let $case = await this.getOrAddCase(AssessmentType.Eligibility);

        const interview = this.interviews?.find(i => i.type?.toUpperCase() === externalInterviewSettings.type?.toUpperCase());
        if (!interview || !$case) {
            return;
        }

        this.instanciateSecondaryInterviewDataProvider(injector);

        const settings = {
            ...this.defaultInterviewSettings,
            package: interview.packageName,
            version: interview.packageVersion,
            type: interview.type,

            mode: AssessmentMode.Secondary,

            // use mode = Primary or Secondary
            isPrimary: false,
            byPassToSummary: true,
            dataProvider: this.interviewDataProvider,
            executionMode: ExecutionMode.Standard,
            contextDataCallback: (async () => ({
                ...await this.interviewDataProvider.generateContextData(),
                tenantId: this.memberService.tenantIdSync
            })).bind(this)
        } as IInterviewSettings;

        await this.interviewHandler.initialize(settings);
    }

    async evaluateSecondaryInterview(settings: IInterviewSettings, variables: IVariableEvaluationModel[]) {

        const interview = this.interviews?.find(i => i.type?.toUpperCase() === settings.type?.toUpperCase());


        if (interview && this.interviewHandler?.initialized) {
            await this.interviewHandler.evaluate(InterviewEventType.Forward, variables);
        }
    }

    get caseCreateProductType() {
        return this.productCategory.toString();
    }

    addCase(): Observable<IProductContextCase> {
        return this.caseService.addCase(this.memberService.base64Sync, this.caseCreateProductType).pipe(switchMap(_ => {
            return from(this.getCurrentProductContext());
        }));
    }

    refreshCase(): Observable<IProductContextCase> {
        return from(this.getCurrentProductContext());
    }

    assignCaseOwnership(owner: string, ownerObjectId: string) {
        return this.caseService.assignCaseOwnership(new AssignCaseOwnershipRequest({ tenantId: this.memberService.tenantIdSync, caseIds: [this.currentCaseSync.id], owner, memberId: this.memberService.memberIdSync, ownerObjectId }))
            .pipe(switchMap(result => {
                return this.refreshCase();
            }),
                catchError(error => {
                    this.alertService.stopLoadingMessage();
                    this.alertService.showStickyMessage(
                        'Case Error',
                        `$Unable to set case owner\r\nError: "${Utilities.getHttpResponseMessages(
                            error
                        )}"`,
                        MessageSeverity.error,
                        error
                    );

                    return of('error');
                }));
    }

    rollbackCaseStage(stageTo: string, reason: string) {
        return this.caseService.rollbackCaseStage(new RollbackCaseStageRequestData(this.memberService.tenantIdSync, this.currentCaseSync.id, this.caseStageSync.destination, stageTo, '', reason))
            .pipe(switchMap(result => {
                return this.refreshCase();
            }),
                catchError(error => {
                    this.alertService.stopLoadingMessage();
                    this.alertService.showStickyMessage(
                        'Case Error',
                        `$Unable to rollback the case\r\nError: "${Utilities.getHttpResponseMessages(
                            error
                        )}"`,
                        MessageSeverity.error,
                        error
                    );

                    return of('error');
                }));
    }

    updateCaseCustomData(metadata: any) {
        return this.currentCase$.pipe(take(1)).pipe(switchMap(productCaseNew => {
            return this.caseService.updateCaseCustomData(this.memberService.base64Sync, productCaseNew.externalId, metadata);
        }));
    }

    async allowEligibility(): Promise<boolean> {
        await this.status$.pipe(first()).toPromise();
        return Promise.resolve(this.statusSync ? this.statusSync.active : false);
    }

    @DataAction()
    setSmartActionContext(payload: SmartActionContext) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: T) => {
            draft.smartActionContext = { ...payload } as SmartActionContext;
        }));
    }

    @DataAction()
    setSmartAction(payload: SmartActionContextItem) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: T) => {
            draft.smartAction = { ...payload } as SmartActionContextItem;
        }));
    }

    @DataAction()
    setProductCategoryContext(productCategoryContext: IProductCategoryContext) {

        let notifications: Notifications;
        if (productCategoryContext) {

            if (productCategoryContext.notifications) {
                notifications = new Notifications(productCategoryContext.notifications);
            }

            const completion = (productCategoryContext.metadata as IProductCategoryMetadata)?.interviews?.find(interview => interview.type === 'Eligibility')?.completion;
            this.setCaseCurrentStateCompletion(completion || 0);
        }

        this.ctx.setState(produce(this.ctx.getState(), (draft: T) => {
            draft.productCategoryContext = productCategoryContext;
            draft.notifications = notifications;
        }));
    }

    clearInterviews() {
        this._interviews = [];
    }
    addInterview(interviewConfig: IInterviewConfig) {
        this._interviews = this.interviews || [];
        this._interviews.push(interviewConfig);

    }

    @DataAction()
    setSourceProduct(product: Product) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: T) => {
            draft.sourceProduct = product;
        }));
    }

    @DataAction()
    setAvailability(@Payload('availability') payload: IProductCategoryAvailability) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: T) => {
            draft.availability = payload;
        }));
    }

    // @DataAction()
    // setAvailableInterviews(@Payload('interviews') payload: IAvailableInterview[]) {
    //     this.ctx.setState(produce(this.ctx.getState(), (draft: T) => {
    //         draft.availableInterviews = payload;
    //     }));
    // }


    @DataAction()
    setCaseCurrentStateCompletion(@Payload('completion') completion: number) {
        if (completion != null) {
            completion = Math.ceil(Math.abs(completion));
        }
        this.ctx.setState(produce(this.ctx.getState(), (draft: T) => {
            const $case = BaseProductService.getCase(draft.productCategoryContext);
            if ($case && $case.stage) {
                $case.stage.assessment = { ...$case.stage.assessment, completion };
            }
        }));
    }





    ///**Member context implementation */
    contextService = inject(ContextDataService);
    setContext(context: string | IContextData): void {
        this.contextService.setContext(context, async (context: string) => {
            if (context.toUpperCase() === this.productCategory.toUpperCase()) {
                const metadata = this.productCategoryContextSync.metadata as { dynamicContextData: IContextData[] };
                return metadata.dynamicContextData?.filter(x => x.id.toUpperCase() === context.toUpperCase()) ?? null;
            } else {
                const metadata = this.productCategoryContextSync.metadata as { smartIcons: ISmartIconSettings[] };
                return metadata.smartIcons.reduce((prev, current) => {
                        prev.push(...(current?.dynamicContextData ?? []));

                    return prev;
                }, [] as IContextData[]).filter(contextData => contextData.id.toUpperCase() === context.toUpperCase()) ?? []
            }
        });
    }
}
